import {
  ConfirmationDialog,
  type ConfirmationDialogProps,
} from '@/components/patterns/confirmation-dialog'
import { Card, CardContent } from '@/components/ui/card'
import { Typography, type TypographyTone } from '@/components/ui/typography'
import dayjs from '@/lib/dayjs'
import {
  getCustomerReceivablesContext,
  type CustomerReceivablesTone,
  type PayLaterProjection,
} from '@/lib/pos/customer-receivables'
import type { PosCartCustomer } from '@/lib/services/carts/carts.types'
import { formatCalendarDate, formatCurrency, normalizeCalendarDateInput } from '@/lib/utils'
import { AlertTriangle, CircleDollarSign, FileClock, type LucideIcon } from 'lucide-react'

const payLaterSummaryTones: Record<CustomerReceivablesTone, TypographyTone> = {
  danger: 'destructive',
  warning: 'warning',
  neutral: 'muted',
}

interface PayLaterSummaryRow {
  label: string
  value: string
  icon: LucideIcon
  tone: CustomerReceivablesTone
  valueTone?: CustomerReceivablesTone | null
}

type PayLaterConfirmationDialogProps = Pick<
  ConfirmationDialogProps,
  | 'title'
  | 'description'
  | 'onConfirm'
  | 'onOpenChange'
  | 'open'
  | 'isPending'
  | 'pendingText'
  | 'confirmText'
  | 'trigger'
> & {
  customer: PosCartCustomer | null | undefined
  /** What the order leaves unpaid. */
  unpaidAmount: number
  /** The customer's open balance after Pay Later, as the server projects it. */
  projection: PayLaterProjection | null
  /**
   * An existing order's due date, which a change keeps as it was placed (null when it has none). A new order is due on
   * the customer's terms from today. Either way, whether it must be paid before pickup or delivery is the customer's
   * rule as it stands.
   */
  orderDueDate?: string | null
}

/** When an existing order the customer may take before paying is due. */
function orderDueDateText(dueDate: string | null): string {
  if (dueDate === null) return 'Payment due when the order is picked up or delivered.'
  const days = dayjs(normalizeCalendarDateInput(dueDate)).diff(dayjs().startOf('day'), 'day')
  const date = formatCalendarDate(dueDate)

  return days > 0
    ? `Payment due in ${days} ${days === 1 ? 'day' : 'days'} (${date}).`
    : days === 0
      ? `Payment due today (${date}).`
      : `Payment was due ${date}.`
}

/**
 * Leaving a balance for later, confirmed against the customer's account: their open and past-due balances, their
 * credit limit, the balance after, and when payment is due. Pay Later at the POS and an order change's "Save, collect
 * later" both ask here.
 */
export function PayLaterConfirmationDialog({
  customer,
  unpaidAmount,
  projection,
  orderDueDate,
  ...dialog
}: PayLaterConfirmationDialogProps) {
  const receivables = getCustomerReceivablesContext({ customer, projection })
  const allowFulfillmentBeforePayment =
    receivables.payLaterAllowed && customer?.allow_fulfillment_before_payment === true
  const paymentTermsDays = Number(customer?.payment_terms_days ?? 0)
  const projectedDueDate =
    allowFulfillmentBeforePayment && paymentTermsDays > 0
      ? dayjs().add(paymentTermsDays, 'day').format('MMM D, YYYY')
      : null
  const paymentTimingText = !allowFulfillmentBeforePayment
    ? 'Payment required before pickup or delivery.'
    : orderDueDate !== undefined
      ? orderDueDateText(orderDueDate)
      : paymentTermsDays > 0
        ? `Payment due in ${paymentTermsDays} ${paymentTermsDays === 1 ? 'day' : 'days'}${
            projectedDueDate ? ` (${projectedDueDate})` : ''
          }.`
        : 'Payment due when the order is picked up or delivered.'
  const { openBalance, overdueBalance, creditLimit, balanceTone, projectedOpenBalance } =
    receivables
  const rows: PayLaterSummaryRow[] = [
    {
      label: 'Open balance',
      value: formatCurrency(openBalance),
      icon: CircleDollarSign,
      tone: openBalance > 0 ? balanceTone : 'neutral',
      valueTone: openBalance > 0 ? balanceTone : null,
    },
    {
      label: 'Past due',
      value: formatCurrency(overdueBalance),
      icon: overdueBalance > 0 ? AlertTriangle : CircleDollarSign,
      tone: overdueBalance > 0 ? 'warning' : 'neutral',
      valueTone: overdueBalance > 0 ? 'warning' : null,
    },
    {
      label: "This order's unpaid amount",
      value: formatCurrency(unpaidAmount),
      icon: CircleDollarSign,
      tone: 'neutral',
    },
    {
      label: 'Credit limit',
      value: creditLimit !== null ? formatCurrency(creditLimit) : 'No limit',
      icon: CircleDollarSign,
      tone: 'neutral',
    },
  ]
  if (projectedOpenBalance !== null) {
    rows.push({
      label: 'Balance after this order',
      value: formatCurrency(projectedOpenBalance),
      icon: CircleDollarSign,
      tone: 'neutral',
    })
  }

  return (
    <ConfirmationDialog
      {...dialog}
      icon={
        <span
          aria-hidden="true"
          className="flex size-9 items-center justify-center rounded-full bg-primary/12 text-interaction ring-1 ring-primary/20 dark:bg-primary/10 dark:text-interaction dark:ring-primary/20"
        >
          <FileClock className="size-4.5" />
        </span>
      }
    >
      <div className="space-y-4">
        <Card surface="subtle" size="sm">
          <CardContent size="sm" className="grid gap-2">
            {rows.map((row) => {
              const Icon = row.icon

              return (
                <div key={row.label} className="flex items-center justify-between gap-4">
                  <Typography
                    as="span"
                    variant="bodySmall"
                    tone={payLaterSummaryTones[row.tone]}
                    className="inline-flex items-center gap-1.5"
                  >
                    <Icon className="size-3.5" />
                    {row.label}
                  </Typography>
                  <Typography
                    as="span"
                    variant="labelStrong"
                    tone={row.valueTone ? payLaterSummaryTones[row.valueTone] : 'default'}
                    className="tabular-nums"
                  >
                    {row.value}
                  </Typography>
                </div>
              )
            })}
          </CardContent>
        </Card>
        <Typography variant="label">{paymentTimingText}</Typography>
      </div>
    </ConfirmationDialog>
  )
}
